import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Typeahead, Input, Button } from '../../components';
import { getClasses, createClass } from '@/services/ClassService';
import { useBts } from '@/context/btsContext/BtsContextProvider';
import ProposalProductTable from './ProposalProductTable';
import { showAlert } from '../../components/common/toastify/ToastContainer';
import { useDebouncedCallback } from '@/utils/useDebouncedCallback';
import { useProduct } from '../../context/productContext/ProductContextProvider';
import CreateProduct from '../setting/product/CreateProduct';
import { ModalProvider } from '../../components/common/modal/ModalContext';
import { useModal } from '../../components/common/modal/ModalContext';

const ProposalProduct = ({ proposalId, goToNextSection }) => {
  const { id } = useParams();
  const [classOptions, setClassOptions] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [productQuery, setProductQuery] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const fileInputRef = React.useRef();
  const productTypeaheadRef = React.useRef();
  const { openModal, closeModal } = useModal();
  const [selectedProduct, setSelectedProduct] = useState([]);

  const {
    resetBtsProduct,
    btsProduct,
    setBtsProduct,
    btsProductInitialState,
    uploadBtsProductFileByClassHandler,
    addManualProduct,
    updateManualProduct,
    fetchBtsProductsByClass, // <-- add this from context
    btsClassProdData,
    exportBTSExcelReport,
    exportParentsInstructionAPI,
    exportPdfAPI,
    exportBtsShReportAPI,
  } = useBts();

  const [isShExporting, setIsShExporting] = useState(false);
  const isProposalLoaded = Boolean(proposalId || id);

  const handleExportShReport = async () => {
    try {
      setIsShExporting(true);
      await exportBtsShReportAPI(proposalId || id); // your service already handles blob download
    } catch (e) {
      handleAlert('error', e);
    } finally {
      setIsShExporting(false);
    }
  };

  const { getAllProduct, proposalProducts } = useProduct();

  const productOption = useMemo(() => {
    return proposalProducts?.map((product) => ({
      label:
        `${product.itemCode} - ${product.itemDescription} - ` +
        `${product.btsPrice == null ? '<strong>BTS Price does not exist</strong>' : product.btsPrice} `,
      value: product.id,
      data: product,
    }));
  }, [proposalProducts]);

  useEffect(() => {
    if (productQuery.trim().length >= 3) {
      getAllProduct(productQuery);
    }
  }, [productQuery]);

  //debouncing
  const debouncedSetSearchQuery = useDebouncedCallback((val) => {
    setProductQuery(val);
  }, 300);

  const handleSearchChange = (query) => {
    debouncedSetSearchQuery.call(query);
  };

  useEffect(() => {
    fetchBtsProductsByClass(
      proposalId || id,
      selectedClass ? selectedClass.id : 0,
    );
  }, [selectedClass, proposalId, id]);

  // Fetch classes on mount
  useEffect(() => {
    getClasses()
      .then((res) =>
        setClassOptions(
          res.map((cls) => ({
            id: cls.id,
            label: cls.className,
            className: cls.className,
          })),
        ),
      )
      .catch(() => setClassOptions([]));
    resetBtsProduct();
  }, []);

  // Centralized alert handler
  const handleAlert = (type, error) => {
    if (type === 'success') return showAlert('success', error);
    if (error && error.response && error.response.data) {
      const data = error.response.data;
      if (Array.isArray(data.errors) && data.errors.length > 0)
        return showAlert('error', data.errors[0]);
      if (typeof data === 'string') return showAlert('error', data);
      if (data.message) return showAlert('error', data.message);
    }
    showAlert('error', error.message || 'Upload failed');
  };

  // Handlers
  const handleClassChange = (selected) => setSelectedClass(selected[0] || null);
  const handleCreateClass = async (newClassName) => {
    try {
      const res = await createClass({ className: newClassName });
      const newClass = {
        id: res.id,
        label: res.className,
        className: res.className,
      };
      setClassOptions((prev) => [...prev, newClass]);
      setSelectedClass(newClass);
      return newClass;
    } catch {
      handleAlert('error', { message: 'Failed to create class' });
      return null;
    }
  };
  const handleProductSelect = (selected) => {
    if (selected && selected.length > 0) {
      const prod = selected[0].data;
      setSelectedProduct([selected[0]]);
      // Set sequenceNumber to max length of btsClassProdData + 1
      setBtsProduct({
        id: 0,
        btsProductId: prod.id,
        schoolProposalId: 0,
        btsClassId: selectedClass ? selectedClass.id : 0,
        className: selectedClass ? selectedClass.className : '',
        itemCode: prod.itemCode,
        description: prod.itemDescription + ' ' + prod.description2,
        conversionFactor: prod.btsConversionFactor || 1,
        price: prod.btsPrice ?? 0,
        normalPrice: prod.normalPrice,
        unit: prod.unit?.trim() || 'Each',
        avgCost: prod.avgCost,
        promoPrice: prod.promoPrice,
        uploadTimestamp: prod.uploadTimestamp,
        sequenceNumber: 0,
      });
    }
  };
  const calculateSellValues = (gp, price, quantity, conversionFactor = 1) => {
    let exSell = 0,
      incSell = 0,
      total = 0,
      totalIncSell = 0,
      totalExSell = 0;
    // Adjust price by conversionFactor
    const adjustedPrice =
      conversionFactor && conversionFactor > 0
        ? price / conversionFactor
        : price;
    if (adjustedPrice > 0 && gp >= 0 && gp < 100) {
      exSell = Math.round((adjustedPrice / (1 - gp / 100)) * 100) / 100;
      incSell = Math.round(exSell * 1.1 * 100) / 100;
      totalExSell = Math.round(exSell * (quantity || 1) * 100) / 100;
      totalIncSell = Math.round(incSell * (quantity || 1) * 100) / 100;
      total = totalIncSell; // for compatibility if used elsewhere
    }
    return { exSell, incSell, total, totalIncSell, totalExSell };
  };
  const handleGpChange = (e) => {
    let gp = parseFloat(e.target.value);
    if (isNaN(gp)) gp = '';
    if (gp > 100) gp = 100;
    const { exSell, incSell, total, totalIncSell, totalExSell } =
      calculateSellValues(
        gp,
        btsProduct.price,
        btsProduct.quantity,
        btsProduct.conversionFactor,
      );
    setBtsProduct({
      ...btsProduct,
      gp,
      exSell,
      incSell,
      total,
      totalIncSell,
      totalExSell,
    });
  };
  const handleQuantityChange = (e) => {
    let quantity = e.target.value === '' ? '' : parseInt(e.target.value, 10);
    if (quantity === '' || isNaN(quantity) || quantity < 1) quantity = '';
    const { exSell, incSell, total, totalIncSell, totalExSell } =
      calculateSellValues(
        btsProduct.gp,
        btsProduct.price,
        quantity,
        btsProduct.conversionFactor,
      );
    setBtsProduct({
      ...btsProduct,
      quantity,
      exSell,
      incSell,
      total,
      totalIncSell,
      totalExSell,
    });
  };
  const handleHeaderChange = (e) =>
    setBtsProduct({ ...btsProduct, header: e.target.value });
  const handleSeqChange = (e) =>
    setBtsProduct({ ...btsProduct, sequenceNumber: e.target.value });

  // Add or Update Product
  const handleAddProduct = async (e) => {
    e.preventDefault();
    let gpError = false;
    let qtyError = false;
    let seqError = false;
    if (btsProduct.gp === undefined || btsProduct.gp === '') gpError = true;
    if (btsProduct.quantity === undefined || btsProduct.quantity === '')
      qtyError = true;
    // Sequence error: seq must not be more than btsClassProdData.length + 1
    const maxSeq =
      btsClassProdData && Array.isArray(btsClassProdData)
        ? btsClassProdData.length + 1
        : 1;
    if (parseInt(btsProduct.sequenceNumber, 10) > maxSeq) seqError = true;

    setBtsProduct({
      ...btsProduct,
      gpError,
      qtyError,
      seqError,
    });

    if (gpError || qtyError || seqError) return;

    if (!btsProduct.price || btsProduct.price == 0) {
      handleAlert('error', {
        message: 'BTS cost for this product does not exist',
      });
      return;
    }
    if (!selectedClass) {
      handleAlert('error', { message: 'Class is required' });
      return;
    }
    try {
      if (btsProduct.id && btsProduct.id > 0) {
        await updateManualProduct(proposalId || id, selectedClass.id);
      } else {
        await addManualProduct(proposalId || id, selectedClass.id);
      }
      await fetchBtsProductsByClass(proposalId || id, selectedClass.id); // refresh table data
      setBtsProduct(btsProductInitialState);
      if (productTypeaheadRef.current) productTypeaheadRef.current.clear();
    } catch (e) {
      handleAlert('error', e);
    }
  };
  // Cancel
  const handleCancel = () => {
    setBtsProduct(btsProductInitialState);
    if (productTypeaheadRef.current) productTypeaheadRef.current.clear();
  };

  // File upload
  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      handleAlert('error', { message: 'Only .csv files are allowed' });
      setUploadFile(null);
      e.target.value = '';
      return;
    }
    setUploadFile(file);
  };
  const handleFileUpload = async () => {
    if (!selectedClass || !uploadFile) return;
    try {
      await uploadBtsProductFileByClassHandler(
        uploadFile,
        proposalId || id,
        selectedClass.id,
      );
      handleAlert('success', 'File uploaded and products refreshed');
      setUploadFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (e) {
      handleAlert('error', e);
    }
  };

  const handleCreateProduct = () => {
    openModal(
      <CreateProduct
        closeModal={closeModal}
        prodCode={productQuery}
        isCreatedFromProposal={true}
      />,
      'Add Product',
    );
  };

  const isEditMode = btsProduct.id && btsProduct.id > 0;
  //////////////
  useEffect(() => {
    if (btsClassProdData && !Array.isArray(btsClassProdData)) {
      setBtsProduct((prev) => ({
        ...prev,
        header: btsClassProdData.header || '',
        btsProductId: btsClassProdData.product?.id || 0,
        itemCode: btsClassProdData.product?.itemCode || '',
        description: btsClassProdData.product?.itemDescription || '',
        price: btsClassProdData.product?.btsPrice ?? 0,
        conversionFactor: btsClassProdData.product?.btsConversionFactor || 1,
      }));

      if (btsClassProdData.product) {
        const prod = btsClassProdData.product;
        setSelectedProduct([
          {
            label: `${prod.itemCode} - ${prod.itemDescription} - ${prod.btsPrice ?? 'No BTS Price'}`,
            value: prod.id,
            data: prod,
          },
        ]);
      }
    }
  }, [btsClassProdData, setBtsProduct, setSelectedProduct]);

  return (
    <div className="p-2">
      <div className="flex mb-6 w-full border-b border-gray-300 pb-2">
        <Typeahead
          options={classOptions}
          labelKey="label"
          label="Class"
          onChange={handleClassChange}
          selected={selectedClass ? [selectedClass] : []}
          handleCreateNew={handleCreateClass}
          createNewLabel="Create new class"
          disabledCreateNew={false}
          placeholder="Select or create a class"
          autoComplete="off"
        />
        <div className="ml-4 min-w-96 ">
          <Typeahead
            options={productOption}
            labelKey="label"
            label="Product"
            onQueryChange={handleSearchChange}
            onChange={handleProductSelect}
            handleCreateNew={handleCreateProduct}
            selected={[]}
            disabledCreateNew={false}
            placeholder="Search and select a product"
            disabled={!selectedClass || btsProduct.btsProductId > 0}
            ref={productTypeaheadRef}
          />
        </div>
        <div className="border-t border-l border-b px-4 py-2 ml-4 rounded-l-xl bg-gray-100/70">
          <Input
            type="file"
            label="Upload Product file"
            disabled={!selectedClass}
            accept=".csv"
            ref={fileInputRef}
            onChange={handleFileInputChange}
          />
        </div>
        <div className="pt-7 border-t border-r border-b px-4 py-2 rounded-r-xl bg-gray-100/70">
          <Button
            onClick={handleFileUpload}
            disabled={!selectedClass || !uploadFile}>
            Upload
          </Button>
        </div>
      </div>
      {btsProduct.btsProductId > 0 && (
        <form
          className="bg-gray-100 rounded-lg"
          onSubmit={(e) => e.preventDefault()}>
          <div className="w-full bg-gray-300 px-4 font-400 rounded-t-lg py-2">
            <span className="font-800 text-blue-600">
              {'['} {btsProduct.itemCode}
              {'] '}
            </span>
            <span className="font-600">{btsProduct?.description}</span>[
            {btsProduct.unit || 'Unit'}]{' '}
          </div>
          <div className="ml-5 flex">
            <Input
              className="w-1/2"
              type="text"
              label="header"
              value={btsProduct.header || ''}
              onChange={(e) =>
                setBtsProduct((prev) => ({ ...prev, header: e.target.value }))
              }
            />
            <Input
              type="text"
              label=" Regular Cost"
              value={btsProduct.avgCost || ''}
              disabled
            />
            <Input
              type="text"
              label="BTS Cost"
              value={btsProduct.price / btsProduct.conversionFactor || ''}
              disabled
            />
          </div>
          <div className="grid grid-cols-4 px-5">
            <Input
              type="number"
              step="0.01"
              label="GP %"
              value={btsProduct.gp === undefined ? '' : btsProduct.gp}
              onChange={handleGpChange}
              error={btsProduct.gpError ? 'GP% is required' : undefined}
              required
            />
            <Input
              type="number"
              label="Quantity"
              value={
                btsProduct.quantity === undefined ? '' : btsProduct.quantity
              }
              min={1}
              onChange={handleQuantityChange}
              error={btsProduct.qtyError ? 'Quantity is required' : undefined}
              required
            />

            <Input
              type="text"
              label="Seq"
              value={btsProduct.sequenceNumber || ''}
              onChange={handleSeqChange}
              disabled={btsProduct.id == 0}
              error={
                btsProduct.seqError
                  ? `Seq cannot be more than ${btsClassProdData.length + 1}`
                  : undefined
              }
            />
          </div>
          <div className="flex justify-between pb-5 pl-5 pr-5">
            <div className="flex w-full">
              <div className="flex gap-10 text-center pl-5 pt-8">
                <span>
                  Inc-Sell = ${' '}
                  {btsProduct.incSell ? btsProduct.incSell.toFixed(2) : '0.00'}
                </span>

                <span className="flex">
                  Ex-Sell = ${' '}
                  {btsProduct.exSell ? btsProduct.exSell.toFixed(2) : '0.00'}
                </span>
              </div>
            </div>
            <div className="flex gap-4 mt-5">
              <div>
                <Button
                  onClick={handleAddProduct}
                  disabled={!selectedClass || !btsProduct.btsProductId}>
                  {btsProduct.id && btsProduct.id > 0
                    ? 'Update'
                    : 'Add Product'}
                </Button>
              </div>
              <div>
                <Button onClick={handleCancel}>Cancel</Button>
              </div>
            </div>
          </div>
        </form>
      )}
      {selectedClass ? (
        <ProposalProductTable proposalId={proposalId} />
      ) : (
        <>
          <div className="font-800 text-center my-10">No Class Selected</div>
        </>
      )}
      <div className="flex justify-between mx-10 mt-10">
        <Button onClick={() => exportBTSExcelReport(proposalId)}>
          Export Excel
        </Button>
        <Button
          disabled={
            !selectedClass ||
            (Array.isArray(btsClassProdData) &&
              btsClassProdData[0] === 'No Data Found')
          }
          onClick={() =>
            exportParentsInstructionAPI(proposalId, selectedClass.id)
          }>
          Export Parents Instruction
        </Button>
        <Button
          disabled={
            !selectedClass ||
            (Array.isArray(btsClassProdData) &&
              btsClassProdData[0] === 'No Data Found')
          }
          onClick={() => exportPdfAPI(proposalId, selectedClass.id)}>
          Book list PDF
        </Button>
        {/* {isProposalLoaded && (
          <Button onClick={handleExportShReport} disabled={isShExporting}>
            {isShExporting ? 'Exporting...' : 'Export Step 1'}
          </Button>
        )} */}
      </div>
    </div>
  );
};

export default ProposalProduct;
